import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  clearStoredTenantSelection,
  buildTenantFromAuthPayload,
  ensureTenantSelection,
  getSubdomainTenantContext,
  getStoredTenant,
  getStoredTenantCatalog,
  persistTenantCatalog,
  setStoredTenantId,
  syncTenantCatalog,
  getStoredSubdomainTenant,
  persistSubdomainTenant,
  clearStoredSubdomainTenant,
  applyTenantBranding,
  resetTenantBranding,
} from "../utils/tenantState";
import {
  getTenantFeatures,
  getTenantSubscription,
  resolvePublicTenant,
  resolveTenantBySubdomain,
  fetchSchoolLogoBlob,
} from "../api/tenants";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { clearAllCaches } from "../utils/requestCache";

// ---------------------------------------------------------------------------
// Subscription normalisation
// Converts any backend subscription shape into a stable object consumed by UI.
// ---------------------------------------------------------------------------
export const normalizeSubscription = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  // New backend shape: { plan, expired, expiryDate, active }
  // Derive status from the `active` boolean when present; fall back to `status` string.
  let status;
  if (raw.expired === true) {
    status = "EXPIRED";
  } else if (raw.active !== undefined) {
    status = raw.active === true ? "ACTIVE" : "EXPIRED";
  } else {
    const candidateStatus = String(raw.status || raw.subscriptionStatus || "").toUpperCase();
    status = ["ACTIVE", "EXPIRED", "CANCELLED", "PAST_DUE", "SUSPENDED"].includes(candidateStatus)
      ? candidateStatus
      : "UNKNOWN";
  }
  const plan = String(
    raw.plan || raw.planName || raw.subscriptionPlan || raw.tier || ""
  ).trim();
  const renewalDate =
    raw.expiryDate
    || raw.renewalDate
    || raw.planExpiry
    || raw.expiresOn
    || raw.subscriptionExpiry
    || raw.subscriptionExpiryDate
    || raw.trialExpiryDate
    || raw.trialExpiresAt
    || raw.trial_expires_at
    || "";
  const dueAmount = Number(raw.dueAmount ?? raw.amount ?? raw.due ?? 0) || 0;
  const whiteLabelEnabled = raw.whiteLabelEnabled === true
    || raw.features?.whiteLabelEnabled === true
    || raw.entitlements?.whiteLabelEnabled === true;
  const quotas = raw.quotas && typeof raw.quotas === "object" ? raw.quotas : {};
  return { status, plan, renewalDate, dueAmount, whiteLabelEnabled, quotas, raw };
};

export const getSubscriptionQuota = (subscription, resourceKey) => {
  const quota = subscription?.quotas?.[resourceKey] || subscription?.raw?.quotas?.[resourceKey];
  if (!quota || typeof quota !== "object") return null;
  const unlimited = quota.unlimited === true || Number(quota.limit) < 0;
  const used = Math.max(0, Number(quota.used) || 0);
  const limit = unlimited ? -1 : Math.max(0, Number(quota.limit) || 0);
  const remaining = unlimited ? null : Math.max(0, Number(quota.remaining ?? (limit - used)) || 0);
  return { used, limit, remaining, unlimited, atLimit: !unlimited && remaining === 0 };
};

// A subscription is blocked when active===false, expired===true, or status!==ACTIVE.
export const isSubscriptionBlocked = (subscription) => {
  // Missing or unrecognised entitlement data is fail-closed. A browser cannot
  // be allowed to infer an active plan from an absent response.
  if (!subscription) return true;
  if (subscription.raw?.active === false) return true;
  if (subscription.raw?.expired === true) return true;
  const s = String(subscription.status || "").toUpperCase();
  return s !== "ACTIVE";
};

// ---------------------------------------------------------------------------
const TenantContext = createContext(null);

export const TenantProvider = ({ children }) => {
  const subdomainContext = getSubdomainTenantContext();
  const unbrandedPlatformTenant = useMemo(() => ({
    id: "platform",
    tenantId: "",
    name: "ClassNX",
    schoolName: "ClassNX",
    subdomain: "",
    schoolLogoUrl: "",
    logoUrl: "",
    faviconUrl: "",
    primaryColor: null,
    secondaryColor: null,
    brandingEnabled: false,
    whiteLabelEnabled: false,
    isSubdomainTenant: false,
  }), []);

  const initialSubdomainTenant = useMemo(() => {
    if (!subdomainContext.isSubdomainTenant) return null;
    const slug = subdomainContext.slug;
    const cached = getStoredSubdomainTenant(slug);
    const catalog = getStoredTenantCatalog();
    const catalogMatch = catalog.find((t) => t.subdomain === slug || t.id === `tenant-${slug}`);
    const existing = cached || catalogMatch;
    const formattedFallback = slug
      ? slug.charAt(0).toUpperCase() + slug.slice(1)
      : "School Campus";
    const resolvedName = existing?.schoolName && existing.schoolName !== "ClassNX"
      ? existing.schoolName
      : existing?.name && existing.name !== "ClassNX"
        ? existing.name
        : formattedFallback;

    return {
      id: existing?.id || (slug ? `tenant-${slug}` : "tenant-subdomain"),
      tenantId: existing?.tenantId || "",
      name: resolvedName,
      schoolName: resolvedName,
      subdomain: slug,
      schoolLogoUrl: existing?.schoolLogoUrl || existing?.logoUrl || "",
      logoUrl: existing?.logoUrl || existing?.schoolLogoUrl || "",
      faviconUrl: existing?.faviconUrl || "",
      primaryColor: existing?.primaryColor || null,
      secondaryColor: existing?.secondaryColor || null,
      brandingEnabled: true,
      whiteLabelEnabled: true,
      isSubdomainTenant: true,
      subscription: existing?.subscription || null,
      plan: existing?.plan || "",
    };
  }, [subdomainContext.isSubdomainTenant, subdomainContext.slug]);

  const [tenantCatalog, setTenantCatalog] = useState(() =>
    getStoredTenantCatalog()
  );
  const [activeTenant, setActiveTenant] = useState(
    () => {
      if (!subdomainContext.isSubdomainTenant) {
        return unbrandedPlatformTenant;
      }
      return initialSubdomainTenant || unbrandedPlatformTenant;
    }
  );
  const [tenantLogoUrl, setTenantLogoUrl] = useState("");
  const logoBlobRevokeRef = useRef("");

  // subscriptionLoading lets the SubscriptionGuard wait before deciding to redirect.
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState("");
  const [tenantFeatures, setTenantFeatures] = useState(null);
  const [tenantFeaturesLoading, setTenantFeaturesLoading] = useState(false);
  const tenantFeaturesRequestRef = useRef(null);
  // subscriptionReady: false until we've fetched status from the server at least once.
  // Gates SubscriptionGuard so it doesn't flash-allow access before the first check.
  const [subscriptionReady, setSubscriptionReady] = useState(
    () => !localStorage.getItem("user")
  );
  const subBootRef = useRef(false);

  // Use a ref to avoid stale-closure setState calls inside async effects.
  const activeTenantRef = useRef(activeTenant);
  useEffect(() => {
    activeTenantRef.current = activeTenant;
  }, [activeTenant]);

  // ------------------------------------------------------------------
  // Tenant school logo resolution helper
  // ------------------------------------------------------------------
  const updateLogoUrl = useCallback(async (tenant) => {
    if (!tenant || tenant?.brandingEnabled === false) {
      if (logoBlobRevokeRef.current) URL.revokeObjectURL(logoBlobRevokeRef.current);
      logoBlobRevokeRef.current = "";
      setTenantLogoUrl("");
      return;
    }

    const directLogo = tenant.logoUrl || tenant.schoolLogoUrl;
    if (directLogo && (directLogo.startsWith("http") || directLogo.startsWith("data:"))) {
      if (logoBlobRevokeRef.current) URL.revokeObjectURL(logoBlobRevokeRef.current);
      logoBlobRevokeRef.current = "";
      setTenantLogoUrl(directLogo);
      return;
    }

    const subCtx = getSubdomainTenantContext();
    if (subCtx.isSubdomainTenant || tenant.whiteLabelEnabled) {
      const blobUrl = await fetchSchoolLogoBlob(directLogo || API_PATHS.admin.schoolLogo());
      if (blobUrl) {
        if (logoBlobRevokeRef.current) URL.revokeObjectURL(logoBlobRevokeRef.current);
        logoBlobRevokeRef.current = blobUrl;
        setTenantLogoUrl(blobUrl);
      } else {
        if (logoBlobRevokeRef.current) URL.revokeObjectURL(logoBlobRevokeRef.current);
        logoBlobRevokeRef.current = "";
        setTenantLogoUrl("");
      }
    } else {
      if (logoBlobRevokeRef.current) URL.revokeObjectURL(logoBlobRevokeRef.current);
      logoBlobRevokeRef.current = "";
      setTenantLogoUrl("");
    }
  }, []);

  useEffect(() => {
    updateLogoUrl(activeTenant);
  }, [activeTenant, updateLogoUrl]);

  useEffect(() => {
    const handleLogoUpdated = () => updateLogoUrl(activeTenantRef.current);
    window.addEventListener("school-logo:updated", handleLogoUpdated);
    return () => window.removeEventListener("school-logo:updated", handleLogoUpdated);
  }, [updateLogoUrl]);

  useEffect(() => {
    return () => {
      if (logoBlobRevokeRef.current) URL.revokeObjectURL(logoBlobRevokeRef.current);
      logoBlobRevokeRef.current = "";
    };
  }, []);

  // ------------------------------------------------------------------
  // Boot: make a same-origin request on app startup to resolve tenant
  // branding by hostname. Do not send subdomain, tenant ID, subscription
  // status, or expiry date. Use response data.brandingEnabled as decision:
  // true: apply the returned school branding fields.
  // false: show ClassNX branding and ignore any cached school branding.
  // ------------------------------------------------------------------
  useEffect(() => {
    const syncedCatalog = syncTenantCatalog(getStoredTenantCatalog());
    setTenantCatalog(syncedCatalog);

    const subCtx = getSubdomainTenantContext();
    if (subCtx.isSubdomainTenant) {
      const slug = subCtx.slug;
      const cached = getStoredSubdomainTenant(slug);
      if (cached) {
        activeTenantRef.current = cached;
        setActiveTenant(cached);
        applyTenantBranding(cached);
      } else if (activeTenantRef.current) {
        applyTenantBranding(activeTenantRef.current);
      }

      const applyResolvedData = (resolved) => {
        if (!resolved) return false;
        const hasRealTenant = Boolean(resolved.tenantId || resolved.schoolName);
        if (!hasRealTenant) return false;

        const current = activeTenantRef.current || {};
        const fallbackName = slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : "";
        const brandingEnabled = resolved.brandingEnabled !== false;
        const whiteLabelEnabled = resolved.whiteLabelEnabled !== false && brandingEnabled;

        if (!brandingEnabled) {
          // Acceptance criteria: When tenant resolution returns brandingEnabled: false,
          // clear cached school logos, colors, and other custom branding. Apply ClassNX defaults immediately.
          clearStoredSubdomainTenant(slug);
          resetTenantBranding();
          if (logoBlobRevokeRef.current) URL.revokeObjectURL(logoBlobRevokeRef.current);
          logoBlobRevokeRef.current = "";
          setTenantLogoUrl("");
          const unbrandedTenant = {
            ...current,
            id: resolved.tenantId || current.id || (slug ? `tenant-${slug}` : "tenant-resolved"),
            tenantId: resolved.tenantId || current.tenantId || "",
            name: "ClassNX",
            schoolName: "ClassNX",
            subdomain: resolved.subdomain || slug,
            schoolLogoUrl: "",
            logoUrl: "",
            faviconUrl: "",
            primaryColor: null,
            secondaryColor: null,
            brandingEnabled: false,
            whiteLabelEnabled: false,
            isSubdomainTenant: false,
          };
          persistSubdomainTenant(slug, unbrandedTenant);
          activeTenantRef.current = unbrandedTenant;
          setActiveTenant(unbrandedTenant);
          return true;
        }

        const resolvedSchoolName = resolved.schoolName || current.schoolName || current.name || fallbackName;
        const resolvedLogo = resolved.logoUrl || resolved.schoolLogoUrl || current.logoUrl || current.schoolLogoUrl || "";
        const merged = {
          ...current,
          id: resolved.tenantId || current.id || (slug ? `tenant-${slug}` : "tenant-resolved"),
          tenantId: resolved.tenantId || current.tenantId || "",
          name: resolvedSchoolName,
          schoolName: resolvedSchoolName,
          subdomain: resolved.subdomain || slug,
          schoolLogoUrl: resolvedLogo,
          logoUrl: resolvedLogo,
          faviconUrl: resolved.faviconUrl || current.faviconUrl || "",
          primaryColor: resolved.primaryColor || current.primaryColor || null,
          secondaryColor: resolved.secondaryColor || current.secondaryColor || null,
          brandingEnabled: true,
          whiteLabelEnabled: true,
          isSubdomainTenant: true,
        };
        persistSubdomainTenant(slug, merged);
        activeTenantRef.current = merged;
        setActiveTenant(merged);
        applyTenantBranding(merged);
        return true;
      };

      resolveTenantBySubdomain(slug)
        .then((resolved) => {
          if (resolved && (resolved.schoolName || resolved.tenantId)) {
            applyResolvedData(resolved);
          } else {
            resolvePublicTenant()
              .then((data) => {
                if (data && (data.schoolName || data.tenantId)) {
                  applyResolvedData(data);
                }
              })
              .catch(() => {});
          }
        })
        .catch(() => {
          if (cached) {
            applyTenantBranding(cached);
          } else if (activeTenantRef.current) {
            applyTenantBranding(activeTenantRef.current);
          }
        });
      return;
    }

    // When on the platform root (not a subdomain):
    // Public unauthenticated visitors must ALWAYS see the clean platform (ClassNX) with no tenant branding.
    const isAuthenticated = Boolean(typeof window !== "undefined" && window.localStorage?.getItem("user"));
    if (!isAuthenticated) {
      activeTenantRef.current = unbrandedPlatformTenant;
      setActiveTenant(unbrandedPlatformTenant);
      resetTenantBranding();
      clearStoredTenantSelection();
      return;
    }

    const stored = activeTenantRef.current;
    const next = stored
      ? syncedCatalog.find((t) => t.id === stored.id) || null
      : null;

    if (next && next.id !== stored?.id) {
      setStoredTenantId(next.id);
      activeTenantRef.current = next;
      setActiveTenant(next);
      applyTenantBranding(next);
    }
  }, []); // eslint-disable-line

  // ------------------------------------------------------------------
  // Logout listener
  // ------------------------------------------------------------------
  useEffect(() => {
    const handleLogout = () => {
      const subCtx = getSubdomainTenantContext();
      if (subCtx.isSubdomainTenant) {
        const cached = getStoredSubdomainTenant(subCtx.slug);
        const retainedTenant = cached || activeTenantRef.current;
        if (retainedTenant) {
          activeTenantRef.current = retainedTenant;
          setActiveTenant(retainedTenant);
          applyTenantBranding(retainedTenant);
        }
        subBootRef.current = false;
        setSubscriptionError("");
        setTenantFeatures(null);
        setTenantFeaturesLoading(false);
        setSubscriptionReady(true);
        return;
      }

      clearStoredTenantSelection();
      resetTenantBranding();
      const fallback = getStoredTenantCatalog();
      setTenantCatalog(fallback);
      activeTenantRef.current = unbrandedPlatformTenant;
      setActiveTenant(unbrandedPlatformTenant);
      subBootRef.current = false;
      setSubscriptionError("");
      setTenantFeatures(null);
      setTenantFeaturesLoading(false);
      setSubscriptionReady(true);
    };
    window.addEventListener("auth:logout", handleLogout);
    return () => window.removeEventListener("auth:logout", handleLogout);
  }, []);

  // ------------------------------------------------------------------
  // refreshSubscriptionStatus – callable by any component post-payment
  // to pull fresh subscription data without a full page reload.
  // ------------------------------------------------------------------
  const refreshSubscriptionStatus = useCallback(async () => {
    let tenant = activeTenantRef.current;
    try {
      const sessionResponse = await api.get(API_PATHS.auth.session, { withCredentials: true });
      const session = sessionResponse?.data?.data ?? sessionResponse?.data ?? {};
      const sessionTenant = buildTenantFromAuthPayload(session, tenant);
      if (sessionTenant?.id) {
        const subCtx = getSubdomainTenantContext();
        tenant = {
          ...(tenant?.id === sessionTenant.id ? tenant : {}),
          ...sessionTenant,
          subdomain: sessionTenant?.subdomain || tenant?.subdomain || subCtx.slug || "",
          schoolName: sessionTenant?.schoolName || tenant?.schoolName || tenant?.name || sessionTenant?.name,
          name: sessionTenant?.schoolName || tenant?.schoolName || tenant?.name || sessionTenant?.name,
          faviconUrl: tenant?.faviconUrl || sessionTenant?.faviconUrl,
          primaryColor: tenant?.primaryColor || sessionTenant?.primaryColor,
          secondaryColor: tenant?.secondaryColor || sessionTenant?.secondaryColor,
          brandingEnabled: sessionTenant.brandingEnabled ?? true,
          whiteLabelEnabled: sessionTenant.whiteLabelEnabled ?? true,
          subscription: sessionTenant.subscription
            ? normalizeSubscription(sessionTenant.subscription)
            : tenant?.subscription,
          isSubdomainTenant: Boolean(subCtx.isSubdomainTenant || tenant?.subdomain || sessionTenant?.subdomain),
        };
        const brandingExplicitlyDisabled =
          sessionTenant.brandingEnabled === false ||
          sessionTenant.whiteLabelEnabled === false ||
          session.whiteLabelEnabled === false ||
          session.brandingEnabled === false;
        if (brandingExplicitlyDisabled) {
          if (subCtx.slug) clearStoredSubdomainTenant(subCtx.slug);
          resetTenantBranding();
          if (logoBlobRevokeRef.current) URL.revokeObjectURL(logoBlobRevokeRef.current);
          logoBlobRevokeRef.current = "";
          setTenantLogoUrl("");
          tenant.brandingEnabled = false;
          tenant.whiteLabelEnabled = false;
          tenant.schoolLogoUrl = "";
          tenant.logoUrl = "";
          tenant.primaryColor = null;
          tenant.secondaryColor = null;
        }
        if (subCtx.isSubdomainTenant && subCtx.slug && !brandingExplicitlyDisabled) {
          persistSubdomainTenant(subCtx.slug, tenant);
        }
        const currentCatalog = getStoredTenantCatalog();
        const nextCatalog = [
          tenant,
          ...currentCatalog.filter((item) => item.id !== tenant.id),
        ];
        persistTenantCatalog(nextCatalog);
        setTenantCatalog(nextCatalog);
        setStoredTenantId(tenant.id);
        activeTenantRef.current = tenant;
        setActiveTenant(tenant);
        if (!brandingExplicitlyDisabled) {
          applyTenantBranding(tenant);
        } else {
          resetTenantBranding();
        }
      }
    } catch {
      // Existing tenant state can still be used for the subscription request;
      // authentication failures are handled by the global session guard.
    }
    if (!tenant?.id) {
      setSubscriptionError("No tenant is associated with this session.");
      return null;
    }
    setSubscriptionLoading(true);
    setSubscriptionError("");
    try {
      const raw = await getTenantSubscription(tenant.id);
      const subscription = normalizeSubscription(raw);
      if (!subscription || subscription.status === "UNKNOWN") {
        const verificationError = new Error("Subscription is not active or could not be verified.");
        setSubscriptionError(verificationError.message);
        throw verificationError;
      }
      const merged = { ...tenant, subscription };
      activeTenantRef.current = merged;
      setActiveTenant(merged);
      setStoredTenantId(merged.id);
      setTenantCatalog((prev) =>
        Array.isArray(prev)
          ? prev.map((t) => (t.id === merged.id ? merged : t))
          : prev
      );
      return subscription;
    } catch (error) {
      setSubscriptionError(error?.message || "We could not verify your subscription status.");
      return null;
    } finally {
      setSubscriptionLoading(false);
    }
  }, []); // stable – reads activeTenantRef so never changes identity

  const refreshTenantFeatures = useCallback(async () => {
    if (tenantFeaturesRequestRef.current) return tenantFeaturesRequestRef.current;
    setTenantFeaturesLoading(true);
    tenantFeaturesRequestRef.current = getTenantFeatures()
      .then((features) => {
        setTenantFeatures(features && typeof features === "object" ? features : {});
        return features;
      })
      .catch(() => {
        // Keep registered platform tabs available when the feature service is unavailable.
        setTenantFeatures(null);
        return null;
      })
      .finally(() => {
        tenantFeaturesRequestRef.current = null;
        setTenantFeaturesLoading(false);
      });
    return tenantFeaturesRequestRef.current;
  }, []);

  const refreshOnAuthenticatedSession = useCallback(({ skipSubscription = false, skipFeatures = false } = {}) => {
    if (subBootRef.current) return;
    subBootRef.current = true;

    if (!localStorage.getItem("user") && !sessionStorage.getItem("user")) {
      setSubscriptionReady(true);
      return;
    }

    try {
      const savedUser = localStorage.getItem("user");
      let parsedUser = savedUser;
      try {
        parsedUser = JSON.parse(savedUser);
      } catch {
        // Login stores the normalized role as a plain string.
      }
      const role = String(parsedUser?.role ?? parsedUser ?? "").toUpperCase().trim();
      if (role === "ROLE_SUPER_ADMIN" || role === "SUPER_ADMIN") {
        setSubscriptionReady(true);
        return;
      }
    } catch {
      // Invalid user state is handled by the global session guard.
    }

    const sessionHasSubscription = Boolean(activeTenantRef.current?.subscription);
    const refreshTasks = [];
    if (!sessionHasSubscription && !skipSubscription) refreshTasks.push(refreshSubscriptionStatus());
    if (!skipFeatures) refreshTasks.push(refreshTenantFeatures());
    if (sessionHasSubscription) setSubscriptionReady(true);
    Promise.allSettled(refreshTasks)
      .finally(() => setSubscriptionReady(true));
  }, [refreshSubscriptionStatus, refreshTenantFeatures]);

  useEffect(() => {
    const handleSessionEstablished = (event) => {
      const subCtx = getSubdomainTenantContext();
      const current = activeTenantRef.current;
      const sessionTenant = buildTenantFromAuthPayload(event?.detail?.session || {}, current);
      const session = event?.detail?.session || {};
      const sessionModules = session.enabledModules || session.modules
        || session.tenant?.enabledModules || session.tenant?.modules || null;

      const brandingExplicitlyDisabled =
        sessionTenant?.brandingEnabled === false ||
        sessionTenant?.whiteLabelEnabled === false ||
        event?.detail?.session?.whiteLabelEnabled === false ||
        event?.detail?.session?.brandingEnabled === false;

      let mergedTenant = null;
      if (subCtx.isSubdomainTenant && current) {
        mergedTenant = {
          ...current,
          ...(sessionTenant || {}),
          id: sessionTenant?.id || current.id,
          tenantId: sessionTenant?.tenantId || sessionTenant?.id || current.tenantId || current.id,
          name: brandingExplicitlyDisabled ? "ClassNX" : (sessionTenant?.schoolName || current.schoolName || current.name),
          schoolName: brandingExplicitlyDisabled ? "ClassNX" : (sessionTenant?.schoolName || current.schoolName || current.name),
          subdomain: subCtx.slug || current.subdomain,
          logoUrl: brandingExplicitlyDisabled ? "" : (sessionTenant?.logoUrl || current.logoUrl),
          schoolLogoUrl: brandingExplicitlyDisabled ? "" : (sessionTenant?.schoolLogoUrl || current.schoolLogoUrl),
          faviconUrl: current.faviconUrl || sessionTenant?.faviconUrl,
          primaryColor: brandingExplicitlyDisabled ? null : (current.primaryColor || sessionTenant?.primaryColor),
          secondaryColor: brandingExplicitlyDisabled ? null : (current.secondaryColor || sessionTenant?.secondaryColor),
          brandingEnabled: !brandingExplicitlyDisabled && (sessionTenant?.brandingEnabled ?? current.brandingEnabled ?? true),
          whiteLabelEnabled: !brandingExplicitlyDisabled && (sessionTenant?.whiteLabelEnabled ?? current.whiteLabelEnabled ?? true),
          subscription: sessionTenant?.subscription
            ? normalizeSubscription(sessionTenant.subscription)
            : current.subscription,
          isSubdomainTenant: !brandingExplicitlyDisabled,
        };
        if (brandingExplicitlyDisabled) {
          if (subCtx.slug) clearStoredSubdomainTenant(subCtx.slug);
          resetTenantBranding();
          if (logoBlobRevokeRef.current) URL.revokeObjectURL(logoBlobRevokeRef.current);
          logoBlobRevokeRef.current = "";
          setTenantLogoUrl("");
        } else if (subCtx.slug) {
          persistSubdomainTenant(subCtx.slug, mergedTenant);
        }
      } else if (sessionTenant?.id) {
        mergedTenant = sessionTenant;
        if (brandingExplicitlyDisabled) {
          mergedTenant.brandingEnabled = false;
          mergedTenant.whiteLabelEnabled = false;
          mergedTenant.logoUrl = "";
          mergedTenant.schoolLogoUrl = "";
          mergedTenant.primaryColor = null;
          mergedTenant.secondaryColor = null;
          resetTenantBranding();
          if (logoBlobRevokeRef.current) URL.revokeObjectURL(logoBlobRevokeRef.current);
          logoBlobRevokeRef.current = "";
          setTenantLogoUrl("");
        }
      }

      if (mergedTenant) {
        const currentCatalog = getStoredTenantCatalog();
        const nextCatalog = [
          mergedTenant,
          ...currentCatalog.filter((tenant) => tenant.id !== mergedTenant.id),
        ];
        persistTenantCatalog(nextCatalog);
        setTenantCatalog(nextCatalog);
        setStoredTenantId(mergedTenant.id);
        activeTenantRef.current = mergedTenant;
        setActiveTenant(mergedTenant);
        if (!brandingExplicitlyDisabled) {
          applyTenantBranding(mergedTenant);
        } else {
          resetTenantBranding();
        }
      }
      subBootRef.current = false;
      setSubscriptionError("");
      setSubscriptionLoading(false);
      setSubscriptionReady(Boolean(mergedTenant?.subscription));
      if (sessionModules && typeof sessionModules === "object") {
        setTenantFeatures({ enabledModules: sessionModules });
      }
      if (!mergedTenant?.subscription || !sessionModules) {
        refreshOnAuthenticatedSession({
          skipSubscription: Boolean(mergedTenant?.subscription),
          skipFeatures: Boolean(sessionModules),
        });
      }
    };

    window.addEventListener("auth:session-established", handleSessionEstablished);
    return () => window.removeEventListener("auth:session-established", handleSessionEstablished);
  }, [refreshOnAuthenticatedSession]);

  useEffect(() => {
    if (!subscriptionReady) return;
    try {
      const role = String(localStorage.getItem("user") || "").replace(/^"|"$/g, "").toUpperCase();
      const isSubscriptionAdmin = ["ROLE_ADMIN", "ROLE_SCHOOL"].includes(role);
      const readOnly = isSubscriptionAdmin
        && Boolean(activeTenant?.subscription)
        && !hasSubscriptionAccess(activeTenant.subscription);
      localStorage.setItem("subscriptionReadOnly", String(readOnly));
    } catch {
      // The backend remains authoritative if browser storage is unavailable.
    }
  }, [activeTenant?.subscription, subscriptionReady]);

  useEffect(() => {
    const handleFeatureDisabled = () => {
      refreshTenantFeatures();
    };
    window.addEventListener("tenant-feature:disabled", handleFeatureDisabled);
    return () => window.removeEventListener("tenant-feature:disabled", handleFeatureDisabled);
  }, [refreshTenantFeatures]);

  useEffect(() => {
    let refreshTimer;
    const handleQuotaChanged = () => {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => refreshSubscriptionStatus(), 150);
    };
    window.addEventListener("tenant-quota:changed", handleQuotaChanged);
    return () => {
      window.clearTimeout(refreshTimer);
      window.removeEventListener("tenant-quota:changed", handleQuotaChanged);
    };
  }, [refreshSubscriptionStatus]);

  // ------------------------------------------------------------------
  const isSubdomainTenant = Boolean(subdomainContext.isSubdomainTenant);
  const subdomainSlug = isSubdomainTenant ? (subdomainContext.slug || activeTenant?.subdomain || "") : "";
  const schoolName = isSubdomainTenant ? (activeTenant?.schoolName || activeTenant?.name || (subdomainSlug ? subdomainSlug.charAt(0).toUpperCase() + subdomainSlug.slice(1) : "")) : "ClassNX";

  const value = useMemo(
    () => ({
      activeTenant,
      tenantCatalog,
      subscriptionLoading,
      subscriptionError,
      subscriptionReady,
      refreshSubscriptionStatus,
      tenantFeatures,
      tenantFeaturesLoading,
      refreshTenantFeatures,

      setActiveTenantById: (tenantId) => {
        const next =
          tenantCatalog.find((t) => t.id === tenantId) || null;
        setStoredTenantId(next?.id || "");
        clearAllCaches();
        activeTenantRef.current = next;
        setActiveTenant(next);
        applyTenantBranding(next);
        subBootRef.current = false;
        setSubscriptionReady(false);
        refreshOnAuthenticatedSession();
      },

      registerTenants: (tenants = []) => {
        const synced = syncTenantCatalog(tenants);
        setTenantCatalog(synced);
        const current = activeTenantRef.current;
        const next = current
          ? synced.find((t) => t.id === current.id) || synced[0] || null
          : synced[0] || null;
        if (next?.id !== current?.id) {
          setStoredTenantId(next?.id || "");
          activeTenantRef.current = next;
          setActiveTenant(next);
          applyTenantBranding(next);
        }
      },

      // Subdomain and tenant branding properties:
      isSubdomainTenant,
      brandingEnabled: activeTenant?.brandingEnabled === true,
      subdomain: subdomainSlug,
      tenantLogoUrl: activeTenant?.brandingEnabled === false ? "" : tenantLogoUrl,
      schoolName,
      primaryColor: activeTenant?.brandingEnabled === false ? null : (activeTenant?.primaryColor || null),
      secondaryColor: activeTenant?.brandingEnabled === false ? null : (activeTenant?.secondaryColor || null),
      faviconUrl: activeTenant?.brandingEnabled === false ? null : (activeTenant?.faviconUrl || null),
      isSubscriptionExpired: Boolean(activeTenant?.subscription) && !hasSubscriptionAccess(activeTenant.subscription),
      isReadOnly: Boolean(
        (Boolean(activeTenant?.subscription) && !hasSubscriptionAccess(activeTenant.subscription))
        || (typeof window !== "undefined" && window.localStorage.getItem("subscriptionReadOnly") === "true")
      ),
    }),
    [
      activeTenant,
      tenantCatalog,
      subscriptionLoading,
      subscriptionError,
      subscriptionReady,
      refreshOnAuthenticatedSession,
      refreshSubscriptionStatus,
      tenantFeatures,
      tenantFeaturesLoading,
      refreshTenantFeatures,
      isSubdomainTenant,
      subdomainSlug,
      tenantLogoUrl,
      schoolName,
    ]
  );

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
};

const PAID_PLAN_NAMES = new Set([
  "ONE_MONTH",
  "THREE_MONTH",
  "SIX_MONTH",
  "ONE_YEAR",
  "STARTER",
  "GROWTH",
  "PROFESSIONAL",
  "PREMIUM",
  "PRO",
  "ENTERPRISE",
]);

export const hasPaidSubscription = (subscription) => {
  if (!subscription || isSubscriptionBlocked(subscription)) return false;
  if (subscription.raw?.entitled === true || subscription.raw?.paid === true) return true;
  return PAID_PLAN_NAMES.has(String(subscription.plan || "").trim().toUpperCase());
};

export const useTenantQuota = (resourceKey) => {
  const { activeTenant } = useTenant();
  return getSubscriptionQuota(activeTenant?.subscription, resourceKey);
};

// Workspace access and paid-plan entitlements are intentionally separate.
// An active FREE/TRIAL tenant may use the workspace until its server-issued
// expiry, but it must not receive premium-only features.
export const hasSubscriptionAccess = (subscription) => {
  if (!subscription) return false;

  const rawStatus = String(subscription.raw?.status || subscription.status || "").trim().toUpperCase();
  if (["EXPIRED", "CANCELLED", "SUSPENDED", "PAST_DUE"].includes(rawStatus)
    || subscription.raw?.cancelled === true
    || subscription.raw?.expired === true
    || subscription.raw?.active === false) {
    return false;
  }

  // The backend owns trial dates and expiry transitions. FREE is a valid active
  // workspace plan and must never be treated as expired merely by plan name.
  return subscription.raw?.active === true || rawStatus === "ACTIVE";
};
